import { BsThreeDots } from "react-icons/bs";
import { Link } from "react-router-dom";
import Dropdown from "../../../components/dropdown/Dropdown";
import { convertToSentenceCase } from "../../../utility";
import { useState } from "react";
import Pagination from "../../../components/pagination";
import useGetSalaryComponents from "../../../hooks/companySettings/salaryStructure/useGetSalaryComponents";
import ConfirmationModal from "../../../components/modal/ConfirmationModal";
import useDeleteSalaryComponent from "../../../hooks/companySettings/salaryStructure/useDeleteSalaryComponent";
import RoundedContainer from "../../../components/containers/RoundedContainer";
import SearchBar from "../../../components/inputs/SearchBar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../../../components/table/Table";
import { Button } from "../../../components/ui";
import LinkButton from "../../../components/ui/LinkButton";

const calculationTypeOptions = [
  { label: "All", value: "" },
  { label: "Fixed", value: "FIXED" },
  { label: "Variable", value: "VARIABLE" },
];

const EarningsSalaryComponents = () => {
  const [currentOpenDropdownId, setCurrentOpenDropdownId] = useState(null);
  const {
    searchQuery,
    componentsList,
    isLoading,
    pagination,
    handlePageClick,
    onCalculationTypeFilterChange,
    onSearch,
  } = useGetSalaryComponents();

  // Logic for deleting component
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [componentIdToDelete, setComponentIdToDelete] = useState(null);

  const { deleteComponent, isDeleting } = useDeleteSalaryComponent({
    onComponentDeleted: () => {
      setShowDeleteConfirmation(false);
      setComponentIdToDelete(null);
    },
  });

  const toggleDeleteConfirmation = (componentId) => {
    setShowDeleteConfirmation(!showDeleteConfirmation);
    setComponentIdToDelete(componentId);
  };

  return (
    <>
      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => toggleDeleteConfirmation(null)}
        title={"Delete Component"}
        description={"Are you sure you want to delete this component?"}
        confirmButtonLabel={"Delete"}
        onConfirm={() => deleteComponent(componentIdToDelete)}
        isLoading={isDeleting}
        loadingMessage="Deleting component..."
      />
      <RoundedContainer className="flex items-center mb-5">
        <SearchBar
          className={"w-80"}
          value={searchQuery}
          onChange={(e) => onSearch(e.target.value)}
        />

        <p className="ml-auto mr-2">Calculation type:</p>
        <select
          className=" rounded-md border border-input px-3 py-1 text-sm shadow-sm"
          onChange={(e) => onCalculationTypeFilterChange(e.target.value)}
        >
          {calculationTypeOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </RoundedContainer>

      <RoundedContainer>
        {isLoading ? (
          <div className="flex justify-center items-center h-40">
            <div className="loader"></div>
            <p className="ml-6">Loading...</p>
          </div>
        ) : !componentsList?.length ? (
          <div className="text-center py-12">
            <p className="text-gray-700 text-2xl font-semibold mb-2">No Salary Components To Show</p>
            <p className="text-gray-500 text-sm mb-5">Add earning components (e.g. Basic, HRA, Allowance) to set up salary structure.</p>
            <LinkButton to="/company-settings/salary-structure/add">
              Add New Component
            </LinkButton>
          </div>
        ) : (
        <Table className="w-full mb-5 table-fixed">
          <TableHead>
            <TableHeaderCell className="w-3/12">
              Name
            </TableHeaderCell>
            <TableHeaderCell className="w-2/12 text-center">
              Calculation Type
            </TableHeaderCell>
            <TableHeaderCell className="w-2/12 text-center">
              Consider for EPF
            </TableHeaderCell>
            <TableHeaderCell className="w-2/12 text-center">
              Consider for ESI
            </TableHeaderCell>
            <TableHeaderCell className="w-2/12 text-center">
              Status
            </TableHeaderCell>
            <TableHeaderCell className="w-1/12 text-center">
              Actions
            </TableHeaderCell>
          </TableHead>
          <TableBody>
            {componentsList?.map((el) => {
              const {
                component_id,
                active,
                consider_for_epf,
                consider_for_esi,
                calculation_type,
                name,
              } = el;
              return (
                <TableRow key={component_id}>
                  <TableCell className="text-dark font-medium text-sm">
                    {name}
                  </TableCell>
                  <TableCell className="text-center text-dark font-medium text-sm">
                    {convertToSentenceCase(calculation_type)}
                  </TableCell>
                  <TableCell className="text-center text-dark font-medium text-sm">
                    {consider_for_epf ? "Yes" : "No"}
                  </TableCell>
                  <TableCell className="text-center text-dark font-medium text-sm">
                    {consider_for_esi ? "Yes" : "No"}
                  </TableCell>
                  <TableCell className="text-center text-dark font-medium text-sm">
                    {parseInt(active) === 1 ? (
                      <span className="bg-green-100 text-green-700 font-medium px-2.5 py-1 rounded-full text-xs">
                        Active
                      </span>
                    ) : (
                      <span className="bg-gray-100 text-gray-700 font-medium px-2.5 py-1 rounded-full text-xs">
                        Inactive
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-center text-dark font-medium text-sm">
                    <div className="relative">
                      <Button
                        className="mx-auto grid place-items-center"
                        variant={"ghost"}
                        size={"icon"}
                        onClick={() =>
                          setCurrentOpenDropdownId(
                            currentOpenDropdownId ? null : component_id
                          )
                        }
                      >
                        <BsThreeDots className=" text-gray-600 w-6 h-6 hover:text-blue-600" />
                      </Button>
                      {component_id === currentOpenDropdownId ? (
                        <Dropdown
                          show={component_id === currentOpenDropdownId}
                          className={
                            "top-6 right-0 w-[9rem] bg-white rounded-l z-10 shadow-lg"
                          }
                          setShow={setCurrentOpenDropdownId}
                        >
                          <ul className="">
                            <li className="hover:bg-gray-100">
                              <LinkButton
                                className={"w-full"}
                                variant={"ghost"}
                                to={`/company-settings/salary-structure/edit/${component_id}`}
                              >
                                Edit
                              </LinkButton>
                            </li>
                            <li className="hover:bg-gray-100 ">
                              <Button
                                className={"w-full"}
                                variant={"ghost"}
                                onClick={() =>
                                  toggleDeleteConfirmation(component_id)
                                }
                              >
                                Delete
                              </Button>
                            </li>
                          </ul>
                        </Dropdown>
                      ) : null}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        )}
      </RoundedContainer>

      {!!componentsList?.length && (
        <RoundedContainer>
          <Pagination
            currentPage={pagination.current_page_number}
            lengthofItems={+pagination.total_count}
            limit={pagination.per_page_limit}
            onPageChange={handlePageClick}
          />
        </RoundedContainer>
      )}
    </>
  );
};

export default EarningsSalaryComponents;
